import { track } from "@amplitude/analytics-react-native";
import { useEffect, useRef } from "react";
import GatheringHasNewCommentsFragmentFragmentDoc from "src/api/graphql/__generated__/documents/GatheringHasNewCommentsFragmentFragmentDoc";
import MarkCommentThreadViewedDocument from "src/api/graphql/__generated__/documents/MarkCommentThreadViewedDocument";
import PhotoHasNewCommentsFragmentFragmentDoc from "src/api/graphql/__generated__/documents/PhotoHasNewCommentsFragmentFragmentDoc";
import useGlobalStore from "src/stores/useGlobalStore";
import { Timeout } from "src/types/time";
import useCommentsThreadStore from "../hooks/useCommentsThreadStore";

const markCommentThreadViewed = async (threadSpecifier: {
	gatheringId?: string;
	photoId?: string;
}) => {
	const { apolloClient } = useGlobalStore.getState();
	if (!apolloClient) return;

	let didClearNotification = false;

	// Optimistic updates to clear notification icon
	if (threadSpecifier.gatheringId) {
		const cacheResult = apolloClient.cache.readFragment({
			fragment: GatheringHasNewCommentsFragmentFragmentDoc,
			id: apolloClient.cache.identify({
				__typename: "Gathering",
				gatheringId: threadSpecifier.gatheringId,
			}),
		});
		if (cacheResult?.hasNewComments === true) {
			didClearNotification = true;
			apolloClient.cache.writeFragment({
				fragment: GatheringHasNewCommentsFragmentFragmentDoc,
				id: apolloClient.cache.identify({
					__typename: "Gathering",
					gatheringId: threadSpecifier.gatheringId,
				}),
				data: {
					...cacheResult,
					gatheringId: threadSpecifier.gatheringId,
					hasNewComments: false,
				},
			});
		}
	}
	if (threadSpecifier.photoId) {
		const cacheResult = apolloClient.cache.readFragment({
			fragment: PhotoHasNewCommentsFragmentFragmentDoc,
			id: apolloClient.cache.identify({
				__typename: "Photo",
				photoId: threadSpecifier.photoId,
			}),
		});
		if (cacheResult?.hasNewComments === true) {
			didClearNotification = true;
			apolloClient.cache.writeFragment({
				fragment: PhotoHasNewCommentsFragmentFragmentDoc,
				id: apolloClient.cache.identify({
					__typename: "Photo",
					photoId: threadSpecifier.photoId,
				}),
				data: {
					...cacheResult,
					photoId: threadSpecifier.photoId,
					hasNewComments: false,
				},
			});
		}
	}

	track("comment_thread_viewed", {
		didClearNotification,
	});
	await apolloClient.mutate({
		mutation: MarkCommentThreadViewedDocument,
		variables: {
			input: {
				...threadSpecifier,
			},
		},
		context: {
			pinBehavior: "inert",
		},
	});
};

const CommentsThreadViewedTracker = ({ isFocused }: { isFocused: boolean }) => {
	const timeout = useRef<Timeout | null>(null);
	const threadSpecifier = useCommentsThreadStore((state) => state.threadSpecifier);
	const topic = useCommentsThreadStore((state) => state.topic);

	useEffect(() => {
		if (!isFocused) return;

		timeout.current = setTimeout(async () => {
			await markCommentThreadViewed(threadSpecifier);
		}, 1000);

		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
				timeout.current = null;
			}
		};
	}, [isFocused, threadSpecifier]);

	useEffect(() => {
		if (!isFocused) return;
		const { unsubscribe } = topic.on("onNewCommentsPolled", async () => {
			await markCommentThreadViewed(threadSpecifier);
		});
		return () => {
			unsubscribe();
		};
	}, [topic, isFocused, threadSpecifier]);

	return null;
};
export default CommentsThreadViewedTracker;
